/* eslint-disable css-modules/no-undef-class */
import { GetServerSideProps, NextPage } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { getDesignerDetail, getProductList } from 'api';
import { NotFoundError } from 'api/utils';
import Container from 'components/Container';
import Grid from 'components/Grid';
import Image from 'components/Image';
import MetaDescription from 'components/MetaDescription';
import MetaTitle from 'components/MetaTitle';
import { LazyPagination } from 'components/Pagination';
import ProductCard from 'components/ProductCard';
import Typography from 'components/Typography';
import Layout from 'containers/Layout';
import Main from 'containers/Main';
import styles from 'styles/pages/Designer.module.css';
import { DesignerDetail } from 'types/Designer';
import { Paginated } from 'types/Paginated';
import { Product } from 'types/Product';
import { makeStringParam, truncate } from 'utils';
import { parsePagination, setPageSize } from 'utils/paginate';

type DesignerPageProps = {
  designer: DesignerDetail;
  products: Paginated<Product>;
  pageSize: number;
};

const DesignerDetailPage: NextPage<DesignerPageProps> = ({ designer, products, pageSize }) => {
  const { firstname, lastname, years, description, image } = designer;
  const router = useRouter();

  const name = `${firstname} ${lastname}`;

  return (
    <>
      <MetaTitle>{name}</MetaTitle>
      <MetaDescription image={image ?? undefined}>
        {truncate(description ?? name, 150)}
      </MetaDescription>
      <Layout>
        <Main paddingTop>
          <Container>
            <article className={styles.bio}>
              {image && (
                <div>
                  <Image src={image} aspectRatio="square" alt={name} className={styles.image} />
                </div>
              )}
              <div>
                <Typography uppercase component="h1" variant="big">
                  {designer.firstname} {designer.lastname}
                </Typography>
                {years && (
                  <Typography component="h2" block uppercase variant="big" color="faded">
                    {years}
                  </Typography>
                )}
                {description && (
                  <Typography className={styles.description} component="p" setInnerHtml>
                    {description}
                  </Typography>
                )}
              </div>
            </article>
          </Container>
          <Container>
            <Grid columns={3}>
              {products.results.map((product, key) => (
                <Link href={`/products/${product.slug}`} key={key} legacyBehavior passHref>
                  <ProductCard product={product} />
                </Link>
              ))}
            </Grid>
          </Container>

          {products.results.length > 0 && (
            <Container>
              <LazyPagination
                data={products}
                pageSize={pageSize}
                setPageSize={s => setPageSize(router, s)}
              />
            </Container>
          )}
        </Main>
      </Layout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ctx => {
  const { slug } = ctx.params || {};
  const { page, pageSize } = parsePagination(ctx.query, ctx.req);

  try {
    const { data: designer } = await getDesignerDetail(makeStringParam(slug));

    const productsResponse = await getProductList({
      designers: [designer.id],
      page,
      page_size: pageSize,
    });

    return {
      props: {
        designer,
        products: productsResponse.data,
        pageSize,
      },
    };
  } catch (error) {
    if (error instanceof NotFoundError) {
      return { notFound: true };
    } else {
      throw error;
    }
  }
};

export default DesignerDetailPage;
